/*
 * Copyright (c) Microsoft Corporation. All rights reserved. Licensed under the MIT license.
 * See LICENSE in the project root for license information.
 */

/* global Office, Excel */

import logger from "../logger";
import { clearRequestCache, cancelAllRequests } from "../functions/requestManager";

Office.onReady((info) => {
  logger.info("Office.onReady - Host:", info.host, "Platform:", info.platform);

  if (info.host === Office.HostType.Excel) {
    Office.actions.associate("cleanExcel", cleanExcel);
    Office.actions.associate("refreshAllCustomFunctions", refreshAllCustomFunctions);
    Office.actions.associate("stopRequests", stopRequests);
    logger.info("Commands registered with Office.actions");
  }
});

async function cleanExcel(event) {
  try {
    await Excel.run(async (context) => {
      const sheet = context.workbook.worksheets.getActiveWorksheet();
      const usedRange = sheet.getUsedRangeOrNullObject();
      usedRange.load("address");
      await context.sync();

      if (!usedRange.isNullObject) {
        usedRange.clear(Excel.ClearApplyTo.all);
        await context.sync();
      }
    });
  } catch (error) {
    logger.error("Error in cleanExcel:", error);
    logger.error("Error message:", error.message);
    logger.error("Error stack:", error.stack);
  } finally {
    event.completed();
  }
}

async function refreshAllCustomFunctions(event) {
  try {
    clearRequestCache();
    await Excel.run(async (context) => {
      const activeSheet = context.workbook.worksheets.getActiveWorksheet();
      activeSheet.load("name");
      await context.sync();
      activeSheet.calculate(true);
      await context.sync();
    });
  } catch (error) {
    logger.error("Error in refreshAllCustomFunctions:", error);
    logger.error("Error message:", error.message);
    logger.error("Error stack:", error.stack);
  } finally {
    event.completed();
  }
}

function stopRequests(event) {
  try {
    cancelAllRequests();
  } catch (error) {
    logger.error("Error in stopRequests:", error);
  } finally {
    event.completed();
  }
}

// Make functions available globally for Shared Runtime
if (typeof globalThis !== "undefined") {
  globalThis.cleanExcel = cleanExcel;
  globalThis.refreshAllCustomFunctions = refreshAllCustomFunctions;
  globalThis.stopRequests = stopRequests;
} else if (typeof window !== "undefined") {
  window.cleanExcel = cleanExcel;
  window.refreshAllCustomFunctions = refreshAllCustomFunctions;
  window.stopRequests = stopRequests;
} else if (typeof global !== "undefined") {
  global.cleanExcel = cleanExcel;
  global.refreshAllCustomFunctions = refreshAllCustomFunctions;
  global.stopRequests = stopRequests;
}
